/**
 * L'ordre de superposition des couches flottantes (#1736).
 *
 * Elles se rendent toutes dans des **portails** accrochés au `body` : elles sont donc frères, et
 * seul le `z-index` les départage. Il était écrit à la main dans chaque composant — `9999` pour la
 * modale, `50` pour un menu de sélection, `100` pour un calendrier — sans que personne n'ait dit
 * lequel passe devant l'autre.
 *
 * D'où le défaut : la liste des organisations s'affichait **derrière** la modale qui la demandait,
 * et aucun job ne pouvait être lancé sur une organisation.
 *
 * **La règle est simple, et c'est elle qui manquait :** un menu ancré est transitoire et attaché à
 * un champ. Il doit passer au-dessus de ce qui porte ce champ — modale comprise — parce qu'il se
 * ferme au clic et ne masque rien durablement. Une modale, elle, passe au-dessus de la page.
 */
export const LAYERS = {
  /**
   * Panneau latéral et son voile — **strictement sous la modale** (#2004).
   *
   * Le barème n'avait que deux rangs, et un panneau n'entrait dans aucun : il n'est pas un menu
   * ancré, lui donner ce rang l'aurait fait passer devant une modale légitime. Il portait donc
   * `z-50`, écrit à la main.
   *
   * ⚠️ **L'ordre panneau/modale a déjà coûté une fois** (#1891) : « `z-50` était celui du panneau
   * latéral : à égalité, c'est l'ordre du DOM qui tranchait, et le panneau passait devant ». Le
   * rang est donc strictement inférieur, et non égal : un panneau se consulte, une modale agit, et
   * la modale ouverte depuis un panneau doit le couvrir.
   */
  sidePanel: 'z-[9990]',
  /** Panneau modal et son voile. Au-dessus de la page et du panneau latéral, sous ce qu'il contient. */
  modal: 'z-[9999]',
  /**
   * Menu ancré à un champ — liste de sélection, calendrier.
   *
   * **Strictement au-dessus de la modale**, et pas à égalité : `PremiumSelect` portait `9999` comme
   * elle, et ne s'en sortait que par l'ordre du DOM. Un portail rendu avant la modale serait
   * repassé dessous sans qu'une ligne de code ait changé.
   */
  anchoredMenu: 'z-[10000]',
  /**
   * Infobulle — **au-dessus de tout le reste** (#2004).
   *
   * Elle portait `z-50` et passait donc **derrière** la modale qui la contient. Cas constaté :
   * `CreateClientModal`, une `FormModalLayout` au rang `modal`, dont l'aide du champ « Numéro de
   * TVA » se rend dans un portail Radix. L'infobulle s'ouvrait, invisible.
   *
   * Elle est au sommet parce qu'elle est la plus fugace de toutes : elle suit le curseur, ne prend
   * aucun clic, et ne masque rien durablement. Une infobulle dans un menu ancré doit se lire comme
   * une infobulle dans une modale.
   */
  tooltip: 'z-[10001]',
} as const;
